import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RemapScheduleTaxRatesDialog } from "@/components/RemapScheduleTaxRatesDialog";
import { formatDateTime, formatNumber } from "@/lib/format";

type RecurringInvoiceSchedule = components["schemas"]["RecurringInvoiceScheduleResponse"];
type TaxRate = components["schemas"]["TaxRateResponse"];

function statusVariant(status: string | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Active":
      return "default";
    case "Paused":
      return "secondary";
    case "Cancelled":
      return "destructive";
    case "Completed":
      return "outline";
    default:
      return "outline";
  }
}

export function RecurringInvoiceSchedulePage() {
  const { scheduleId } = useParams<{ scheduleId: string }>();

  const [schedule, setSchedule] = useState<RecurringInvoiceSchedule | null>(null);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [remapOpen, setRemapOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scheduleId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchSchedule() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/admin/recurring-invoices/{id}", {
        params: { path: { id: scheduleId! } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load recurring invoice schedule.");
      } else {
        setSchedule(data ?? null);
      }
      setLoading(false);
    }

    fetchSchedule();
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  const organisationId = schedule?.organisationId;
  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;

    async function fetchTaxRates() {
      const { data } = await api.GET("/admin/tax-rates", {
        params: { query: { organisationId: organisationId! } },
      });
      if (!cancelled) setTaxRates(data ?? []);
    }

    fetchTaxRates();
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const taxRateById = useMemo(() => {
    const map = new Map<string, TaxRate>();
    for (const rate of taxRates) if (rate.id) map.set(rate.id, rate);
    return map;
  }, [taxRates]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Recurring invoice schedule</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : schedule ? (
        <>
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <span>{schedule.customer?.name ?? "Schedule"}</span>
              <Badge variant={statusVariant(schedule.status)}>{schedule.status}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Section title="Schedule">
              <Field label="Schedule ID">
                <Badge variant="secondary" className="font-mono text-xs">
                  {schedule.id}
                </Badge>
              </Field>
              <Field label="Organisation">
                <Link to={`/organisations/${schedule.organisationId}`}>
                  <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                    {schedule.organisationId}
                  </Badge>
                </Link>
              </Field>
              <Field label="Customer">
                <Link
                  to={`/customers/${schedule.customerId}?organisationId=${schedule.organisationId}`}
                  className="hover:underline"
                >
                  {schedule.customer?.name ?? (
                    <span className="font-mono text-xs">{schedule.customerId}</span>
                  )}
                </Link>
              </Field>
              <Field label="Currency">{schedule.currency}</Field>
              <Field label="Cadence">{schedule.cadence}</Field>
              <Field label="Auto-send">
                <Badge variant={schedule.autoSend ? "default" : "outline"}>
                  {schedule.autoSend ? "Yes" : "No"}
                </Badge>
              </Field>
              <Field label="Memo" className="sm:col-span-3">
                {schedule.memo ?? <span className="text-muted-foreground">—</span>}
              </Field>
            </Section>

            <Section title="Recurrence">
              <Field label="Start date">{formatDateTime(schedule.startDate)}</Field>
              <Field label="Next run">
                {schedule.nextRunAt ? (
                  formatDateTime(schedule.nextRunAt)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Last generated">
                {schedule.lastGeneratedAt ? (
                  formatDateTime(schedule.lastGeneratedAt)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Occurrences generated">
                {formatNumber(schedule.occurrencesGenerated)}
              </Field>
              <Field label="Total occurrences">
                {schedule.totalOccurrences == null ? (
                  <span className="text-muted-foreground">Unbounded</span>
                ) : (
                  formatNumber(schedule.totalOccurrences)
                )}
              </Field>
            </Section>

            <Section title="Audit">
              <Field label="Created">{formatDateTime(schedule.createdAt)}</Field>
              <Field label="Created by">
                <ActorLink actorId={schedule.createdBy} />
              </Field>
              <Field label="Updated">{formatDateTime(schedule.updatedAt)}</Field>
              <Field label="Updated by">
                <ActorLink actorId={schedule.updatedBy} />
              </Field>
            </Section>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Line items
                </h3>
                {(schedule.lineItems?.length ?? 0) > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setRemapOpen(true)}>
                    Remap tax rates
                  </Button>
                )}
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit price override</TableHead>
                      <TableHead>Tax rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(schedule.lineItems?.length ?? 0) === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-muted-foreground h-16 text-center">
                          No line items.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (schedule.lineItems ?? []).map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="text-sm">
                            <Link
                              to={`/products/${item.productId}?organisationId=${schedule.organisationId}`}
                            >
                              <span className="hover:underline">
                                {item.productName ?? `${item.productId?.slice(0, 8)}...`}
                              </span>
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm">{formatNumber(item.quantity)}</TableCell>
                          <TableCell className="text-sm">
                            {item.unitPriceOverride == null ? (
                              <span className="text-muted-foreground">Product price</span>
                            ) : (
                              formatNumber(item.unitPriceOverride)
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            <TaxRateCell taxRateId={item.taxRateId} taxRateById={taxRateById} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
        <RemapScheduleTaxRatesDialog
          schedule={schedule}
          taxRates={taxRates}
          open={remapOpen}
          onOpenChange={setRemapOpen}
          onUpdated={setSchedule}
        />
        </>
      ) : (
        <p className="text-muted-foreground">Recurring invoice schedule not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function TaxRateCell({
  taxRateId,
  taxRateById,
}: {
  taxRateId: string | null | undefined;
  taxRateById: Map<string, TaxRate>;
}) {
  if (!taxRateId) return <span className="text-muted-foreground">—</span>;
  const rate = taxRateById.get(taxRateId);
  if (rate) {
    return (
      <span>
        {rate.code} <span className="text-muted-foreground">({rate.rate}%)</span>
      </span>
    );
  }
  // Referenced rate isn't in the org's current rates — it was deleted. This is what makes a
  // schedule fail template validation; flag it so it's obvious what needs remapping.
  return (
    <Badge variant="destructive" className="font-mono text-xs">
      Missing: {taxRateId.slice(0, 8)}…
    </Badge>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
